import { z } from 'zod';

type SessionStorage = Pick<Storage, 'getItem' | 'setItem'>;
const key = (workspaceId: string) => `mio.workspace.${workspaceId}.editor`;

/** Stored identities are reusable only while this document holds their Web Lock. */
export function createEditorSessions(
  storage: SessionStorage,
  locks?: LockManager,
  sharedStorage?: SessionStorage,
) {
  const sessions = new Map<string, Promise<string>>();
  const allocated = new Map<string, string>();
  const held = new Set<string>();
  const recoveries = new Map<string, Promise<{ sessionId: string; commit(): void }>>();
  const reclaims = new Map<string, Promise<string>>();
  const releases = new Map<string, () => void>();
  let disposed = false;

  function claim(id: string): Promise<boolean> {
    if (!locks) return Promise.resolve(true);
    return new Promise((resolve, reject) => {
      void locks
        .request(`mio.ide.document.${id}`, { ifAvailable: true }, (lock) => {
          if (disposed) throw new Error('IDE session owner was closed. Reload this page.');
          if (!lock) {
            resolve(false);
            return;
          }
          return new Promise<void>((release) => {
            held.add(id);
            releases.set(id, release);
            resolve(true);
          });
        })
        .catch(reject);
    });
  }

  function release(id: string) {
    releases.get(id)?.();
    releases.delete(id);
    held.delete(id);
  }

  function read(store: SessionStorage | undefined, workspaceId: string): string | null {
    try {
      const parsed = z.uuid().safeParse(store?.getItem(key(workspaceId)));
      return parsed.success ? parsed.data : null;
    } catch {
      return null;
    }
  }

  async function allocate(workspaceId: string) {
    // Without cross-document locks, reuse cannot be distinguished from a cloned
    // tab. Use a fresh identity and let the server require explicit recovery.
    let id: string | null = null;
    if (locks) {
      // Only a successful server launch publishes the shared identity. Prefer it
      // over a tab's earlier rejected candidate, then support existing tab storage.
      const candidates = new Set([read(sharedStorage, workspaceId), read(storage, workspaceId)]);
      for (const candidate of candidates) {
        if (candidate && (await claim(candidate))) {
          id = candidate;
          break;
        }
      }
    }
    if (!id) {
      id = crypto.randomUUID();
      if (!(await claim(id))) throw new Error('Could not reserve this IDE session. Retry.');
    }
    if (disposed) throw new Error('IDE session owner was closed. Reload this page.');
    allocated.set(workspaceId, id);
    try {
      storage.setItem(key(workspaceId), id);
    } catch {
      // The identity remains stable in this document.
    }
    return id;
  }

  function forWorkspace(workspaceId: string): Promise<string> {
    if (disposed)
      return Promise.reject(new Error('IDE session owner was closed. Reload this page.'));
    let session = sessions.get(workspaceId);
    if (!session) {
      session = allocate(workspaceId);
      sessions.set(workspaceId, session);
      void session.catch(() => sessions.delete(workspaceId));
    }
    return session;
  }

  /**
   * A document that opened beside another tab keeps a rejected candidate for its
   * whole lifetime, yet the accepted identity in shared storage becomes claimable
   * the moment the document that owned it closes. Adopting it lets the server
   * renew the same owner at once, as a page reload would, instead of waiting for
   * the runtime to prove the previous browser absent. A candidate whose lock is
   * still held stays with its live owner; this document keeps its own identity.
   */
  function reclaim(workspaceId: string): Promise<string> {
    if (disposed)
      return Promise.reject(new Error('IDE session owner was closed. Reload this page.'));
    let reclaiming = reclaims.get(workspaceId);
    if (!reclaiming) {
      reclaiming = (async () => {
        const current = await forWorkspace(workspaceId);
        if (!locks || !sharedStorage) return current;
        const candidate = read(sharedStorage, workspaceId);
        if (!candidate || candidate === current || held.has(candidate)) return current;
        if (!(await claim(candidate))) return current;
        if (disposed) throw new Error('IDE session owner was closed. Reload this page.');
        sessions.set(workspaceId, Promise.resolve(candidate));
        allocated.set(workspaceId, candidate);
        try {
          storage.setItem(key(workspaceId), candidate);
        } catch {
          // The adopted identity remains stable in this document.
        }
        release(current);
        return candidate;
      })();
      reclaims.set(workspaceId, reclaiming);
      void reclaiming.finally(() => reclaims.delete(workspaceId)).catch(() => undefined);
    }
    return reclaiming;
  }

  function beginRecovery(workspaceId: string) {
    if (disposed)
      return Promise.reject(new Error('IDE session owner was closed. Reload this page.'));
    let recovery = recoveries.get(workspaceId);
    if (!recovery) {
      recovery = (async () => {
        await forWorkspace(workspaceId);
        const sessionId = crypto.randomUUID();
        if (!(await claim(sessionId)))
          throw new Error('Could not reserve this IDE session. Retry.');
        if (disposed) throw new Error('IDE session owner was closed. Reload this page.');
        let committed = false;
        return {
          sessionId,
          commit() {
            if (committed) return;
            if (disposed) throw new Error('IDE session owner was closed. Reload this page.');
            // A failed launch keeps the old editor identity available for saves.
            // Retries reuse this candidate, including after a lost API response.
            sessions.set(workspaceId, Promise.resolve(sessionId));
            allocated.set(workspaceId, sessionId);
            try {
              storage.setItem(`mio.workspace.${workspaceId}.editor`, sessionId);
            } catch {
              // Storage-disabled browsers keep the committed in-memory identity.
            }
            committed = true;
            recoveries.delete(workspaceId);
          },
        };
      })();
      recoveries.set(workspaceId, recovery);
      void recovery.catch(() => recoveries.delete(workspaceId));
    }
    return recovery;
  }

  return {
    forWorkspace,
    reclaim,
    beginRecovery,
    /** Call only after the server accepts this document's launch. IDs are not credentials. */
    remember(workspaceId: string, sessionId: string) {
      if (disposed || allocated.get(workspaceId) !== sessionId || !held.has(sessionId)) return;
      try {
        sharedStorage?.setItem(key(workspaceId), sessionId);
      } catch {
        // Shared storage is optional; the document and tab identities still work.
      }
    },
    dispose() {
      disposed = true;
      for (const id of [...releases.keys()]) release(id);
      sessions.clear();
      allocated.clear();
      held.clear();
      recoveries.clear();
      reclaims.clear();
    },
  };
}
